import {
	API_TIMEOUT,
	CACHE_DURATION,
	CACHE_KEYS,
	ENDPOINT_BY_HOST,
	LOG_PREFIX,
	READY_PROMISE_NAME,
} from './constants.js';
import { getCachedValue } from './get-cached-value.js';

/**
 * Geolocation Detection - this fetches geolocation data from the API and caches it in localStorage.
 */
class GeolocationDetection {
	constructor() {
		const { hostname } = window.location;
		const match = ENDPOINT_BY_HOST.find(([sub]) => hostname.includes(sub));
		this.apiEndpoint = match
			? match[1]
			: '/wp-json/growtherapy/v1/geolocation/';

		let resolveReady;
		window[READY_PROMISE_NAME] = new Promise((resolve) => {
			resolveReady = resolve;
		});

		this.fetchGeolocationData(resolveReady);
	}

	/**
	 * Fetches geolocation data from the API.
	 * If there is cached data, do nothing. Otherwise, fetch the data from the API and cache it.
	 * Calls resolveReady when data is available (from cache or after fetch completes).
	 *
	 * @param {Function} resolveReady - Called once when consumers can safely read from cache.
	 * @return {Promise<void>} Does not return anything
	 */
	async fetchGeolocationData(resolveReady) {
		try {
			if (this.hasFullCache()) {
				// eslint-disable-next-line no-console
				console.debug(LOG_PREFIX, 'Found cached geolocation data');
				return;
			}

			const controller = new AbortController();
			const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

			const response = await fetch(this.apiEndpoint, {
				signal: controller.signal,
			});

			clearTimeout(timeoutId);

			if (!response.ok) return;

			const data = await response.json();
			if (!data || typeof data !== 'object') {
				// eslint-disable-next-line no-console
				console.debug(LOG_PREFIX, 'No geolocation data found');
				return;
			}

			for (const config of Object.values(CACHE_KEYS)) {
				const value = data[config.apiKey];
				if (value) {
					this.setCachedValue(value, config.key, config.expiryKey);
				}
			}

			// eslint-disable-next-line no-console
			console.debug(LOG_PREFIX, 'Fetched geolocation data');
		} catch (error) {
			// eslint-disable-next-line no-console
			console.debug(LOG_PREFIX, 'Error fetching geolocation data:', error);
		} finally {
			resolveReady();
		}
	}

	/**
	 * Returns true if all geolocation values are present and valid in cache.
	 * Stops at first missing/invalid entry to avoid unnecessary localStorage reads.
	 *
	 * @return {boolean} True if all geolocation values are present and valid in cache, otherwise false.
	 */
	hasFullCache() {
		for (const { key, expiryKey } of Object.values(CACHE_KEYS)) {
			if (!getCachedValue(key, expiryKey)) return false;
		}

		return true;
	}

	/**
	 * Sets a value and its expiry date in local storage.
	 *
	 * @param {string} value     - The value to set.
	 * @param {string} valueKey  - The key of the value to set.
	 * @param {string} expiryKey - The key of the expiry date to set.
	 * @return {void} Does not return anything
	 */
	setCachedValue(value, valueKey, expiryKey) {
		try {
			const expiry = Date.now() + CACHE_DURATION;
			localStorage.setItem(valueKey, value);
			localStorage.setItem(expiryKey, expiry.toString());
		} catch (error) {
			// eslint-disable-next-line no-console
			console.debug(LOG_PREFIX, 'Error setting cached value:', error);
		}
	}
}

new GeolocationDetection();
